import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Users, Car, UsersRound, Film, ImageOff } from 'lucide-react';
import XTAMSpinner from '../components/XTAMSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

export interface AnalyticsGalleryListItem {
  id: string;
  hostSerial: string;
  cameraExternalId: string;
  devicesCameraId: number | null;
  eventTsMs: number;
  eventTime: string;
  persons: number;
  vehicles: number;
  isCrowd: boolean;
  detectionCount: number;
  hasImage: boolean;
  siteId: number | null;
  siteName: string | null;
  cameraName: string | null;
}

export type GalleryEventScope = '' | 'persons' | 'vehicles' | 'crowd';

const VEHICLE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todos los tipos' },
  { value: 'car', label: 'Auto' },
  { value: 'truck', label: 'Camión' },
  { value: 'bus', label: 'Autobús' },
  { value: 'motorcycle', label: 'Motocicleta' },
  { value: 'van', label: 'Furgoneta' },
  { value: 'bicycle', label: 'Bicicleta' },
  { value: 'suv', label: 'SUV' },
];

function formatLocal(dt: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return dt;
  }
}

function resolveAnalyticsRange(fromRaw: string, toRaw: string): {
  fromStr: string;
  toStr: string;
  corrected: boolean;
} {
  const parseLocal = (s: string): Date | null => {
    const t = s.trim();
    if (!t) return null;
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  let toD = parseLocal(toRaw);
  let fromD = parseLocal(fromRaw);
  let corrected = false;

  if (!toD) {
    toD = new Date();
    corrected = true;
  }
  if (!fromD) {
    fromD = new Date(toD.getTime() - 60 * 60 * 1000);
    corrected = true;
  }
  if (fromD.getTime() > toD.getTime()) {
    fromD = new Date(toD.getTime() - 60 * 60 * 1000);
    corrected = true;
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  const fromStr = `${fromD.getFullYear()}-${pad(fromD.getMonth() + 1)}-${pad(fromD.getDate())}T${pad(fromD.getHours())}:${pad(fromD.getMinutes())}`;
  const toStr = `${toD.getFullYear()}-${pad(toD.getMonth() + 1)}-${pad(toD.getDate())}T${pad(toD.getHours())}:${pad(toD.getMinutes())}`;
  if (!corrected && (fromStr !== fromRaw.trim() || toStr !== toRaw.trim())) corrected = true;
  return { fromStr, toStr, corrected };
}

const toIso = (localDatetimeLocal: string) => {
  const d = new Date(localDatetimeLocal);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

type AnalyticsGalleryTabProps = {
  from: string;
  to: string;
  siteName: string;
  /** Se incrementa al pulsar Buscar en el padre (misma lógica que la pestaña tabla para sitio). */
  searchNonce: number;
  onFromToCorrected: (fromStr: string, toStr: string) => void;
  onOpenDetail: (row: AnalyticsGalleryListItem) => void;
  goToRecording: (row: AnalyticsGalleryListItem) => void;
  recordingBlockedReason: (row: AnalyticsGalleryListItem) => string | null;
  /** Comparte object URLs con el drawer para no volver a descargar la misma imagen. */
  getCachedEventImageUrl: (eventId: string) => string | null;
  registerEventImageUrl: (eventId: string, objectUrl: string) => void;
};

const GalleryThumb: React.FC<{
  eventId: string;
  hasImage: boolean;
  onOpen: () => void;
  getCachedEventImageUrl: (eventId: string) => string | null;
  registerEventImageUrl: (eventId: string, objectUrl: string) => void;
}> = ({ eventId, hasImage, onOpen, getCachedEventImageUrl, registerEventImageUrl }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setFailed(false);
    if (!hasImage) {
      setSrc(null);
      return;
    }

    const cached = getCachedEventImageUrl(eventId);
    if (cached) {
      setSrc(cached);
      setLoading(false);
      return;
    }

    setSrc(null);
    if (objectUrlRef.current) {
      objectUrlRef.current = null;
    }

    const el = containerRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        obs.disconnect();
        const again = getCachedEventImageUrl(eventId);
        if (again) {
          setSrc(again);
          return;
        }
        const token = localStorage.getItem('token');
        if (!token) return;
        setLoading(true);
        axios
          .get(`${API_URL}/analytics/events/${eventId}/image`, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((r) => {
            if (r.data && r.data.size > 0) {
              const u = URL.createObjectURL(r.data);
              registerEventImageUrl(eventId, u);
              objectUrlRef.current = u;
              setSrc(u);
            } else setFailed(true);
          })
          .catch(() => setFailed(true))
          .finally(() => setLoading(false));
      },
      { rootMargin: '120px', threshold: 0.01 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      objectUrlRef.current = null;
    };
  }, [eventId, hasImage, getCachedEventImageUrl, registerEventImageUrl]);

  return (
    <button
      type="button"
      className="analytics-gallery-thumb-wrap"
      onClick={onOpen}
      aria-label="Abrir detalle del evento"
    >
      <div ref={containerRef} className="analytics-gallery-thumb-frame">
        {!hasImage && (
          <div className="analytics-gallery-thumb-placeholder">
            <ImageOff size={28} aria-hidden />
            <span>Sin imagen</span>
          </div>
        )}
        {hasImage && loading && !src && (
          <div className="analytics-gallery-thumb-skeleton" aria-hidden />
        )}
        {hasImage && failed && !src && (
          <div className="analytics-gallery-thumb-placeholder analytics-gallery-thumb-placeholder--error">
            <ImageOff size={28} aria-hidden />
            <span>Error al cargar</span>
          </div>
        )}
        {src && <img src={src} alt="" className="analytics-gallery-thumb-img" decoding="async" />}
      </div>
    </button>
  );
};

export const AnalyticsGalleryTab: React.FC<AnalyticsGalleryTabProps> = ({
  from,
  to,
  siteName,
  searchNonce,
  onFromToCorrected,
  onOpenDetail,
  goToRecording,
  recordingBlockedReason,
  getCachedEventImageUrl,
  registerEventImageUrl,
}) => {
  const [eventScope, setEventScope] = useState<GalleryEventScope>('vehicles');
  const [vehicleColorPreset, setVehicleColorPreset] = useState('');
  const [vehicleObjectType, setVehicleObjectType] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [items, setItems] = useState<AnalyticsGalleryListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paramsRef = useRef({
    from,
    to,
    siteName,
    eventScope,
    vehicleColorPreset,
    vehicleObjectType,
    page,
    limit,
  });
  paramsRef.current = {
    from,
    to,
    siteName,
    eventScope,
    vehicleColorPreset,
    vehicleObjectType,
    page,
    limit,
  };

  const fetchGallery = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    const p = paramsRef.current;
    const resolved = resolveAnalyticsRange(p.from, p.to);
    if (resolved.corrected) {
      onFromToCorrected(resolved.fromStr, resolved.toStr);
      return;
    }
    const fromIso = toIso(resolved.fromStr);
    const toIsoVal = toIso(resolved.toStr);
    if (!fromIso || !toIsoVal) {
      setError('Rango de fechas inválido');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        from: fromIso,
        to: toIsoVal,
        page: String(p.page),
        limit: String(p.limit),
        sortBy: 'event_time',
        sortDir: 'desc',
      });
      if (p.siteName.trim()) params.set('siteName', p.siteName.trim());
      if (p.eventScope) params.set('eventScope', p.eventScope);
      if (p.eventScope === 'vehicles') {
        if (p.vehicleColorPreset.trim()) params.set('vehicleColorPreset', p.vehicleColorPreset.trim());
        if (p.vehicleObjectType.trim()) params.set('vehicleObjectType', p.vehicleObjectType.trim());
      }

      const res = await axios.get(`${API_URL}/analytics/events?${params.toString()}`);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        setItems([]);
        setTotal(0);
        setError(null);
        return;
      }
      let msg = 'No se pudieron cargar los eventos';
      if (axios.isAxiosError(e) && e.response?.data) {
        const d = e.response.data as { error?: string | { message?: string }; message?: string };
        if (typeof d.error === 'string') msg = d.error;
        else if (d.error && typeof d.error === 'object' && d.error.message) msg = String(d.error.message);
        else if (d.message) msg = String(d.message);
      }
      setError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [onFromToCorrected]);

  useEffect(() => {
    void fetchGallery();
  }, [
    from,
    to,
    searchNonce,
    eventScope,
    vehicleColorPreset,
    vehicleObjectType,
    page,
    fetchGallery,
  ]);

  const toggleScope = (next: Exclude<GalleryEventScope, ''>) => {
    setPage(1);
    setEventScope((s) => (s === next ? '' : next));
    if (next !== 'vehicles') {
      setVehicleColorPreset('');
      setVehicleObjectType('');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <section className="inv-toolbar analytics-toolbar analytics-gallery-toolbar">
        <div className="analytics-gallery-quick-filters" role="group" aria-label="Filtro rápido por tipo de evento">
          <span className="analytics-gallery-quick-label">Filtro rápido</span>
          <div className="analytics-quick-chips">
            <button
              type="button"
              className={`analytics-quick-chip ${eventScope === 'persons' ? 'analytics-quick-chip--active' : ''}`}
              onClick={() => toggleScope('persons')}
              aria-pressed={eventScope === 'persons'}
              title="Eventos con al menos una persona"
            >
              <Users size={20} aria-hidden />
              <span>Personas</span>
            </button>
            <button
              type="button"
              className={`analytics-quick-chip ${eventScope === 'vehicles' ? 'analytics-quick-chip--active' : ''}`}
              onClick={() => toggleScope('vehicles')}
              aria-pressed={eventScope === 'vehicles'}
              title="Eventos con al menos un vehículo"
            >
              <Car size={20} aria-hidden />
              <span>Vehículos</span>
            </button>
            <button
              type="button"
              className={`analytics-quick-chip ${eventScope === 'crowd' ? 'analytics-quick-chip--active' : ''}`}
              onClick={() => toggleScope('crowd')}
              aria-pressed={eventScope === 'crowd'}
              title="Solo aglomeración / multitud"
            >
              <UsersRound size={20} aria-hidden />
              <span>Multitudes</span>
            </button>
          </div>
        </div>

        {eventScope === 'vehicles' && (
          <div className="analytics-gallery-vehicle-filters">
            <label className="analytics-field analytics-field-grow">
              <span>Color de vehículo</span>
              <select
                className="inv-select analytics-color-select"
                value={vehicleColorPreset}
                onChange={(e) => {
                  setPage(1);
                  setVehicleColorPreset(e.target.value);
                }}
                aria-label="Color de vehículo"
              >
                <option value="">Todos los colores</option>
                <option value="rojo">Rojo</option>
                <option value="negro">Negro</option>
                <option value="azul">Azul</option>
                <option value="amarillo">Amarillo</option>
                <option value="gris">Gris</option>
                <option value="verde">Verde</option>
                <option value="blanco">Blanco</option>
                <option value="morado">Morado</option>
                <option value="naranja">Naranja</option>
                <option value="otro">Otro</option>
              </select>
            </label>
            <label className="analytics-field analytics-field-grow">
              <span>Tipo de vehículo</span>
              <select
                className="inv-select analytics-color-select"
                value={vehicleObjectType}
                onChange={(e) => {
                  setPage(1);
                  setVehicleObjectType(e.target.value);
                }}
                aria-label="Tipo de vehículo"
              >
                {VEHICLE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {error && <div className="analytics-error">{error}</div>}
      </section>

      <div className="analytics-gallery-grid-wrap">
        {loading && items.length === 0 ? (
          <div className="analytics-gallery-loading">
            <XTAMSpinner variant="inline" message="Cargando eventos…" />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state analytics-gallery-empty">
            <p>Sin eventos en este rango o filtros</p>
          </div>
        ) : (
          <ul className="analytics-gallery-grid">
            {items.map((row) => {
              const recBlock = recordingBlockedReason(row);
              return (
                <li key={row.id} className="analytics-gallery-card">
                  <GalleryThumb
                    eventId={row.id}
                    hasImage={row.hasImage}
                    onOpen={() => onOpenDetail(row)}
                    getCachedEventImageUrl={getCachedEventImageUrl}
                    registerEventImageUrl={registerEventImageUrl}
                  />
                  <div className="analytics-gallery-card-body">
                    <div className="analytics-gallery-card-meta">
                      <span className="analytics-gallery-site">{row.siteName ?? '—'}</span>
                      <span className="analytics-gallery-camera">{row.cameraName ?? row.cameraExternalId}</span>
                      <time className="analytics-gallery-time" dateTime={row.eventTime}>
                        {formatLocal(row.eventTime)}
                      </time>
                    </div>
                    <div className="analytics-gallery-card-actions">
                      <button
                        type="button"
                        className="analytics-linkish"
                        onClick={() => onOpenDetail(row)}
                      >
                        Detalle
                      </button>
                      <button
                        type="button"
                        className="analytics-linkish"
                        onClick={() => goToRecording(row)}
                        disabled={row.devicesCameraId == null || row.siteId == null}
                        title={recBlock ?? 'Abrir grabación'}
                      >
                        <Film size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        Grabación
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="analytics-pagination">
        <button
          type="button"
          className="btn-refresh"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          aria-label="Página anterior"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="analytics-page-meta">
          Página {page} / {totalPages} · {total} eventos
        </span>
        <button
          type="button"
          className="btn-refresh"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((p) => p + 1)}
          aria-label="Página siguiente"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </>
  );
};
